import { useEffect, useState } from 'react'
import { IconoMoverEtapa, IconoCerrar, IconoCheck } from '@/shared/ui/Iconos/Iconos'
import estilos from './PlanProyectos.module.css'

type EtapaOpcion = {
  id: string
  orden: number
  nombre: string
  modulos?: { id: string }[]
}

type PropsModalMover = {
  abierto: boolean
  onCerrar: () => void
  moduloCodigo: string
  moduloNombre: string
  etapaActualId: string
  etapas: EtapaOpcion[]
  onConfirmarMover: (etapaDestinoId: string) => void
}

export function ModalMoverAEtapa({
  abierto,
  onCerrar,
  moduloCodigo,
  moduloNombre,
  etapaActualId,
  etapas,
  onConfirmarMover,
}: PropsModalMover) {
  const [destinoSeleccionado, setDestinoSeleccionado] = useState<string>('')

  useEffect(() => {
    if (!abierto) {
      setDestinoSeleccionado('')
      return
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [abierto, onCerrar])

  if (!abierto) return null

  const otrasEtapas = etapas
    .filter((e) => e.id !== etapaActualId)
    .sort((a, b) => a.orden - b.orden)

  const etapaActual = etapas.find((e) => e.id === etapaActualId)

  function handleMover() {
    if (!destinoSeleccionado) return
    onConfirmarMover(destinoSeleccionado)
    onCerrar()
  }

  return (
    <div
      className={estilos.modalMoverOverlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCerrar()
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="titulo-modal-mover"
    >
      <div className={estilos.modalMoverContenedor}>
        <header className={estilos.modalMoverHeader}>
          <div className={estilos.modalMoverTituloContenedor}>
            <span className={estilos.modalMoverIcono}>
              <IconoMoverEtapa />
            </span>
            <h3 id="titulo-modal-mover" className={estilos.modalMoverTitulo}>
              Mover a otra etapa
            </h3>
          </div>
          <button
            type="button"
            className={estilos.modalMoverBtnCerrar}
            onClick={onCerrar}
            title="Cerrar modal"
            aria-label="Cerrar"
          >
            <IconoCerrar />
          </button>
        </header>

        <div className={estilos.modalMoverInfoModulo}>
          <span className={estilos.modalMoverEtiquetaModulo}>
            Módulo seleccionado:
          </span>
          <div className={estilos.modalMoverDetalleModulo}>
            <b>{moduloCodigo}</b> — {moduloNombre}
          </div>
          {etapaActual && (
            <div className={estilos.modalMoverEtapaActual}>
              Etapa actual: <span>Etapa {etapaActual.orden} · {etapaActual.nombre}</span>
            </div>
          )}
        </div>

        <div className={estilos.modalMoverCuerpo}>
          <label className={estilos.modalMoverSubtitulo}>
            Selecciona la etapa de destino:
          </label>

          {otrasEtapas.length === 0 ? (
            <p className={estilos.modalMoverVacio}>
              No hay otras etapas disponibles en este proyecto.
            </p>
          ) : (
            <div className={estilos.modalMoverListaEtapas}>
              {otrasEtapas.map((et) => {
                const esSeleccionada = destinoSeleccionado === et.id
                const claseOpcion = [
                  estilos.modalMoverOpcionEtapa,
                  esSeleccionada ? estilos.modalMoverOpcionActiva : '',
                ].filter(Boolean).join(' ')

                return (
                  <button
                    key={et.id}
                    type="button"
                    className={claseOpcion}
                    onClick={() => setDestinoSeleccionado(et.id)}
                  >
                    <div className={estilos.modalMoverOpcionIzq}>
                      <span className={estilos.modalMoverBadgeOrden}>
                        Etapa {et.orden}
                      </span>
                      <span className={estilos.modalMoverNombreEtapa}>
                        {et.nombre}
                      </span>
                    </div>

                    <div className={estilos.modalMoverOpcionDer}>
                      <span className={estilos.modalMoverConteo}>
                        {et.modulos?.length ?? 0} módulos
                      </span>
                      {esSeleccionada && (
                        <span className={estilos.modalMoverCheck}>
                          <IconoCheck />
                        </span>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        <footer className={estilos.modalMoverFooter}>
          <button
            type="button"
            className={estilos.modalMoverBtnCancelar}
            onClick={onCerrar}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={estilos.modalMoverBtnConfirmar}
            disabled={!destinoSeleccionado}
            onClick={handleMover}
          >
            <IconoCheck />
            Mover a esta etapa
          </button>
        </footer>
      </div>
    </div>
  )
}
